import {
    VStack,
    Text,
    Flex,
    Spinner,
    Center,
    HStack,
    Menu,
    MenuButton,
    MenuList,
    Input,
    InputGroup,
    InputLeftElement,
    Box,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { useGetNotifications, NotificationItem, NotificationType } from '../../hooks/useGetNotifications';
import { useMarkNotificationRead } from '../../hooks/useMarkNotificationRead';
import { useOpenNotificationTarget } from '../../hooks/useOpenNotificationTarget';
import { useMarkAllNotificationsRead } from '../../hooks/useMarkAllNotificationsRead';
import { useToggleNotifications } from '../../hooks/useToggleNotifications';
import { useMarkNegotiationRead } from '../../hooks/invoice/useInvoiceNegotiation';
import { invoicesListHref } from '../../shared/invoicesListViewState';
import NotificationRow from './NotificationRow';
import EMSDetailModal from '../../modals/EMSDetailModal';
import AppDrawer from '../AppDrawer';
import {
    Button,
    Icon,
    IconButton,
    MenuItemRow,
    Tooltip,
    menuSurfaceSx,
} from '../../ui';

interface NotificationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

type ReadFilter = 'all' | 'unread' | 'read';
type TypeFilter = 'all' | NotificationType;

/**
 * Message notification types — surfaced via the New Chat badge + /chat surface, not the
 * bell drawer. Kept out of the feed, type filter, and "Mark all as read" for all roles.
 */
const MESSAGE_NOTIFICATION_TYPES: NotificationType[] = ['order-mention', 'vendor-message'];

const typeFilterLabels: Record<TypeFilter, string> = {
    'all': 'All Types',
    'new-order': 'New Order',
    'quotes-extended': 'Quotes Extended',
    'auto-requote': 'Auto Requote',
    'quotes-back': 'Quotes Back',
    'quotes-back-potential-total': 'Quotes Back (Potential Total)',
    'quotes-back-quotes-only': 'Quotes Back (Quotes Only)',
    'no-quoted-parts': 'No Quotes',
    'flagged-parts': 'Flagged Parts',
    'orders-confirmed': 'Confirmed',
    'orders-rejected': 'Rejected',
    'orders-returned-cancelled': 'Return/Cancel',
    'rekeying-completed': 'Re-key Done',
    'rekeying-completed-flagged': 'Re-key Flagged',
    'rekeying-skipped': 'Re-key Skipped',
    'tesla-order-error': 'Tesla Error',
    'part-number-changed': 'Part # Changed',
    'pre-order-backorder': 'Pre-order Backorder',
    'back-order-ordered': 'Backorder Ordered',
    'no-oem-quotes': 'No OEM Quotes',
    'order-mention': 'Messages & Mentions',
    'invoice-mention': 'Invoice comment',
    'vendor-message': 'Vendor Messages',
    'supplier-changes': 'Supplier Changes',
    'needs-rekey': 'Re-key Needed',
    'rekey-advisor-score': 'Advisor Score',
    'order-not-confirmed': 'Not Confirmed',
    'order-request-enabled': 'Order Request',
    'flagged-invoices': 'Flagged invoice',
    'negotiation-handoff': 'Negotiation needs reply',
    'order-placement-failed': 'Order Failed',
    'ccc-manual-po-created': 'CCC Manual PO Created',
    'ccc-manual-po-failed': 'CCC Manual PO Failed',
    'rekey-quote-only-completed': 'Re-key Quote Only Done',
    'inventory-stock-request': 'Purchase Order',
    'inventory-stock-request-placed': 'In Stock Ordered',
};

const NotificationDrawer = ({ isOpen, onClose }: NotificationDrawerProps) => {
    const navigate = useNavigate();
    const openNotificationTarget = useOpenNotificationTarget();
    const markAsRead = useMarkNotificationRead();
    const markNegotiationRead = useMarkNegotiationRead();
    const toggleNotifications = useToggleNotifications();
    const [selectedEmsId, setSelectedEmsId] = useState<string | null>(null);
    const [isEmsModalOpen, setIsEmsModalOpen] = useState(false);

    // Search and pagination states
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [allNotifications, setAllNotifications] = useState<NotificationItem[]>([]);

    // Filter states
    const [readFilter, setReadFilter] = useState<ReadFilter>('all');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

    // Mark all as read with optimistic update for local state.
    // Message types are never touched here -- they're read via the chat surface, not the bell.
    const markAllAsRead = useMarkAllNotificationsRead(() => {
        setAllNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, undefined, MESSAGE_NOTIFICATION_TYPES.join(','));

    // Reset filters when drawer opens (keep notifications to avoid flicker)
    useEffect(() => {
        if (isOpen) {
            setSearchInput('');
            setDebouncedSearch('');
            setOffset(0);
            setReadFilter('all');
            setTypeFilter('all');
        }
    }, [isOpen]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setOffset(0);
            setAllNotifications([]);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { data, isLoading, isFetching } = useGetNotifications(isOpen, debouncedSearch, offset);

    // Accumulate notifications for pagination (also sync when drawer opens)
    useEffect(() => {
        if (isOpen && data?.notifications) {
            if (offset === 0) {
                setAllNotifications(data.notifications);
            } else {
                setAllNotifications(prev => [...prev, ...data.notifications]);
            }
        }
    }, [isOpen, data?.notifications, offset]);

    // Map notification type to order page tab
    const getInitialStatusForNotification = (type: NotificationType): 'newParts' | 'quoting' | 'selection' | 'ordered' | 'rekey' => {
        switch (type) {
            case 'new-order':
                return 'newParts';
            case 'quotes-extended':
            case 'auto-requote':
                return 'quoting';
            case 'quotes-back':
            case 'quotes-back-potential-total':
            case 'quotes-back-quotes-only':
            case 'no-quoted-parts':
            case 'flagged-parts':
            case 'part-number-changed':
            case 'pre-order-backorder':
            case 'order-request-enabled':
                return 'selection';
            case 'orders-confirmed':
            case 'orders-rejected':
            case 'orders-returned-cancelled':
            case 'tesla-order-error':
            case 'order-not-confirmed':
                return 'ordered';
            case 'rekeying-completed':
            case 'rekeying-completed-flagged':
            case 'rekeying-skipped':
            case 'rekey-quote-only-completed':
                return 'rekey';
            case 'supplier-changes':
                return 'ordered';
            case 'needs-rekey':
                return 'rekey';
            case 'flagged-invoices':
                return 'newParts';
            case 'order-placement-failed':
            case 'ccc-manual-po-created':
            case 'ccc-manual-po-failed':
                return 'ordered';
            case 'inventory-stock-request-placed':
                return 'ordered';
            default:
                return 'newParts';
        }
    };

    const handleNotificationClick = (notification: NotificationItem) => {
        if (notification.data.type === 'negotiation') {
            markNegotiationRead.mutate(notification.data.negotiationId);
            openNotificationTarget(
                notification,
                invoicesListHref({
                    type: 'all',
                    showArchived: false,
                    showNegotiation: true,
                    selectedNegotiationId: notification.data.negotiationId,
                    currentPage: 1,
                }),
                { source: 'notifications' },
            );
            onClose();
            return;
        }

        if (!notification.read) {
            markAsRead.mutate({ notificationId: notification._id });
        }

        if (notification.data.type === 'ems') {
            setSelectedEmsId(notification.data.emsId);
            setIsEmsModalOpen(true);
        } else if (notification.data.type === 'invoice') {
            openNotificationTarget(notification, `/invoices/${notification.data.invoiceId}`, {
                source: 'notifications',
            });
            onClose();
        } else if (
            notification.type === 'inventory-stock-request' ||
            notification.data.type === 'stock-request' ||
            notification.data.type === 'purchase-order'
        ) {
            const purchaseOrderId =
                notification.data.type === 'purchase-order'
                    ? notification.data.purchaseOrderId
                    : notification.data.type === 'stock-request'
                        ? notification.data.stockRequestId
                        : undefined;
            const qs = purchaseOrderId
                ? `?purchaseOrderId=${encodeURIComponent(purchaseOrderId)}`
                : '';
            openNotificationTarget(notification, `/inventory/purchase-orders${qs}`, {
                source: 'notifications',
            });
            onClose();
        } else {
            const initialStatus = getInitialStatusForNotification(notification.type);
            openNotificationTarget(notification, `/order/${notification.data.orderId}`, {
                source: 'notifications',
                initialStatus,
            });
            onClose();
        }
    };

    const handleEmsModalClose = () => {
        setIsEmsModalOpen(false);
        setSelectedEmsId(null);
    };

    const filteredNotifications = useMemo(() => {
        // Messages (order-mention + vendor-message) live in the chat surface, not the bell drawer, for all roles.
        let notifications = allNotifications.filter(n => !MESSAGE_NOTIFICATION_TYPES.includes(n.type));

        if (readFilter === 'unread') {
            notifications = notifications.filter(n => !n.read);
        } else if (readFilter === 'read') {
            notifications = notifications.filter(n => n.read);
        }

        if (typeFilter !== 'all') {
            notifications = notifications.filter(n => n.type === typeFilter);
        }

        return notifications;
    }, [allNotifications, readFilter, typeFilter]);

    // Count unread in filtered results
    const filteredUnreadCount = useMemo(() => {
        return filteredNotifications.filter(n => !n.read).length;
    }, [filteredNotifications]);

    // Check if any filters are active
    const hasActiveFilters = debouncedSearch || readFilter !== 'all' || typeFilter !== 'all';

    // Use API's total unread count when no filters, otherwise use filtered count
    const displayUnreadCount = hasActiveFilters ? filteredUnreadCount : (data?.unreadCount ?? 0);

    // Filter type dropdown options based on enabled notification types
    const availableTypeFilters = useMemo(() => {
        const prefs = data?.enabledNotificationTypes;
        let filters: TypeFilter[];
        // If no prefs (undefined or empty object), show all types
        if (!prefs || Object.keys(prefs).length === 0) {
            filters = Object.keys(typeFilterLabels) as TypeFilter[];
        } else {
            // Derive types where 'notif' channel is enabled
            const enabledTypes = (Object.entries(prefs) as [NotificationType, string[]][])
                .filter(([, channels]) => channels.includes('notif'))
                .map(([type]) => type);
            if (enabledTypes.length === 0) {
                filters = Object.keys(typeFilterLabels) as TypeFilter[];
            } else {
                // Always include 'all' option, plus the enabled types.
                filters = ['all', ...enabledTypes] as TypeFilter[];
            }
        }
        // Messages (order-mention + vendor-message) live in the chat surface, not the bell drawer, for all roles.
        return filters.filter(t => !MESSAGE_NOTIFICATION_TYPES.includes(t as NotificationType));
    }, [data?.enabledNotificationTypes]);

    const notificationsEnabled = data?.notificationsEnabled !== false;

    return (
        <>
            <AppDrawer
                isOpen={isOpen}
                onClose={onClose}
                size="md"
                title={
                    <HStack spacing={2}>
                        <Text fontSize="lg" fontWeight="bold" color="fg.default">
                            Notifications
                        </Text>
                        <Tooltip
                            label={data?.notificationsEnabled ? 'Click to disable notifications' : 'Click to enable notifications'}
                            placement="top"
                        >
                            <IconButton
                                aria-label="Toggle notifications"
                                icon={<Icon name={data?.notificationsEnabled ? 'bell' : 'bellOff'} size="sm" />}
                                size="xs"
                                variant={data?.notificationsEnabled ? 'solid' : 'outline'}
                                colorScheme={data?.notificationsEnabled ? 'brand' : 'red'}
                                onClick={() => toggleNotifications.mutate()}
                                isLoading={toggleNotifications.isPending}
                            />
                        </Tooltip>
                    </HStack>
                }
                contentProps={{ overflow: 'hidden' }}
                bodyProps={{ p: 0, pt: 0, px: 0, py: 0 }}
                headerRight={
                    <>
                        {notificationsEnabled && (
                            <>
                                <Menu>
                                    <MenuButton
                                        as={IconButton}
                                        icon={<Icon name="filter" size="sm" />}
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        color={typeFilter !== 'all' ? 'brand.solid' : 'fg.muted'}
                                        aria-label="Filter by type"
                                    />
                                    <MenuList
                                        p="6px"
                                        minW="160px"
                                        zIndex={2000}
                                        maxH="300px"
                                        overflowY="auto"
                                        sx={menuSurfaceSx}
                                    >
                                        {availableTypeFilters.map((type) => (
                                            <MenuItemRow
                                                key={type}
                                                kind="single"
                                                isSelected={typeFilter === type}
                                                onClick={() => setTypeFilter(type)}
                                            >
                                                {typeFilterLabels[type]}
                                            </MenuItemRow>
                                        ))}
                                    </MenuList>
                                </Menu>
                                {displayUnreadCount > 0 && (
                                    <Button
                                        size="xs"
                                        variant="solid"
                                        colorScheme="brand"
                                        fontWeight="normal"
                                        px={2}
                                        onClick={() => markAllAsRead.mutate()}
                                        isLoading={markAllAsRead.isPending}
                                    >
                                        Mark {displayUnreadCount} as read
                                    </Button>
                                )}
                            </>
                        )}
                        <Tooltip label="Notification settings" placement="top">
                            <IconButton
                                aria-label="Notification settings"
                                icon={<Icon name="settings" size="sm" />}
                                size="toolbar"
                                variant="ghost"
                                colorScheme="gray"
                                color="fg.muted"
                                onClick={() => {
                                    navigate('/settings/notifications');
                                    onClose();
                                }}
                            />
                        </Tooltip>
                    </>
                }
                headerBelow={
                    notificationsEnabled ? (
                        <VStack align="stretch" spacing={3} px={6} py={3} borderBottomWidth="1px" borderColor="border.subtle">
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none" h="36px">
                                    <Icon name="search" size="sm" color="fg.muted" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search by VIN or RO#..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    bg="white"
                                    h="36px"
                                    fontSize="sm"
                                    fontWeight="normal"
                                    focusBorderColor="brand.500"
                                />
                            </InputGroup>

                            <HStack spacing={2}>
                                <Button
                                    size="sm"
                                    variant={readFilter === 'unread' ? 'solid' : 'outlineGray'}
                                    onClick={() => setReadFilter(readFilter === 'unread' ? 'all' : 'unread')}
                                    flex={1}
                                >
                                    Unread
                                </Button>
                                <Button
                                    size="sm"
                                    variant={readFilter === 'read' ? 'solid' : 'outlineGray'}
                                    onClick={() => setReadFilter(readFilter === 'read' ? 'all' : 'read')}
                                    flex={1}
                                >
                                    Read
                                </Button>
                            </HStack>
                        </VStack>
                    ) : undefined
                }
            >
                {isLoading && offset === 0 ? (
                    <Center h="200px">
                        <Spinner color="brand.solid" size="lg" />
                    </Center>
                ) : data?.notificationsEnabled === false ? (
                    <Center h="200px">
                        <Flex direction="column" align="center" gap={3}>
                            <Icon name="bellOff" size="xl" color="error.fg" />
                            <Text color="fg.default" fontSize="sm" fontWeight="medium">
                                Notifications are paused
                            </Text>
                            <Text color="fg.muted" fontSize="xs" textAlign="center" maxW="200px">
                                You won't receive new notifications until you re-enable them.
                            </Text>
                            <Button
                                size="sm"
                                variant="solid"
                                colorScheme="brand"
                                onClick={() => toggleNotifications.mutate()}
                                isLoading={toggleNotifications.isPending}
                                leftIcon={<Icon name="bell" size="sm" />}
                            >
                                Enable Notifications
                            </Button>
                        </Flex>
                    </Center>
                ) : filteredNotifications.length === 0 ? (
                    <Center h="200px">
                        <Flex direction="column" align="center" gap={2}>
                            <Icon name="bell" size="lg" color="fg.muted" />
                            <Text color="fg.muted" fontSize="sm">
                                {debouncedSearch
                                    ? 'No notifications found for this search'
                                    : allNotifications.length === 0
                                        ? 'No notifications yet'
                                        : 'No notifications match filters'}
                            </Text>
                            {(allNotifications.length !== 0 || debouncedSearch) && (
                                <Button
                                    size="sm"
                                    variant="link"
                                    colorScheme="brand"
                                    onClick={() => {
                                        setSearchInput('');
                                        setReadFilter('all');
                                        setTypeFilter('all');
                                    }}
                                >
                                    Clear filters
                                </Button>
                            )}
                        </Flex>
                    </Center>
                ) : (
                    <VStack spacing={0} align="stretch">
                        {filteredNotifications.map((notification) => (
                            <NotificationRow
                                key={notification._id}
                                notification={notification}
                                onClick={() => handleNotificationClick(notification)}
                            />
                        ))}
                        {data?.hasMore && (
                            <Box p={4} borderTopWidth="1px" borderColor="border.subtle">
                                <Button
                                    w="100%"
                                    size="sm"
                                    variant="outline"
                                    colorScheme="brand"
                                    onClick={() => setOffset(prev => prev + 20)}
                                    isLoading={isFetching && offset > 0}
                                >
                                    View More
                                </Button>
                            </Box>
                        )}
                    </VStack>
                )}
            </AppDrawer>

            {selectedEmsId && (
                <EMSDetailModal
                    isOpen={isEmsModalOpen}
                    onClose={handleEmsModalClose}
                    emsId={selectedEmsId}
                />
            )}
        </>
    );
};

export default NotificationDrawer;
